import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

import {
  FiX,
  FiBriefcase,
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiChevronDown,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiPlus,
  FiShield
} from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import { settingsService } from '../../services/settingsService';
import { useClient } from '../../context/ClientContext';

/*
 * =========================================================
 * FORM STATE
 * =========================================================
 */

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  country: '',
  industry: '',
  entity_type: '',
  currency: '',
  fiscal_year_end: '',
  tax_id: '',
  year_started: ''
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/*
 * =========================================================
 * VALIDATION
 * =========================================================
 */

const validateForm = (form) => {
  const name = form.name.trim();

  if (name.length < 2 || name.length > 120) {
    return 'Business name must be between 2 and 120 characters.';
  }

  if (
    form.email.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
  ) {
    return 'Please enter a valid business email.';
  }

  if (
    form.phone.trim() &&
    form.phone.replace(/\D/g, '').length < 7
  ) {
    return 'Please enter a valid phone number.';
  }

  if (form.website.trim()) {
    try {
      const url = new URL(
        /^https?:\/\//i.test(form.website.trim())
          ? form.website.trim()
          : `https://${form.website.trim()}`
      );

      if (
        !['http:', 'https:'].includes(url.protocol) ||
        !url.hostname.includes('.')
      ) {
        return 'Please enter a valid website.';
      }
    } catch {
      return 'Please enter a valid website.';
    }
  }

  if (form.currency.trim()) {
    const currency = form.currency.trim().toUpperCase();

    if (!/^[A-Z]{3}$/.test(currency)) {
      return 'Enter a three-letter currency code.';
    }

    try {
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency
      });
    } catch {
      return 'Please enter a valid currency code.';
    }
  }

  if (form.year_started.trim()) {
    const year = Number(form.year_started);
    const currentYear = new Date().getFullYear();

    if (
      !Number.isInteger(year) ||
      year < 1800 ||
      year > currentYear
    ) {
      return `Year started must be between 1800 and ${currentYear}.`;
    }
  }

  if (
    form.tax_id.trim() &&
    !/^[A-Za-z0-9][A-Za-z0-9\s-]{2,30}$/.test(
      form.tax_id.trim()
    )
  ) {
    return 'Tax ID contains invalid characters.';
  }

  return null;
};

/*
 * =========================================================
 * INPUT COMPONENT
 * =========================================================
 */

function FormField({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  icon: Icon,
  maxLength
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={`client-${name}`}
        className="block text-xs font-semibold text-slate-700"
      >
        {label}

        {required && (
          <span className="ml-1 text-rose-500">*</span>
        )}
      </label>

      <div className="relative">
        {Icon && (
          <SafeIcon
            icon={Icon}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400"
          />
        )}

        <input
          id={`client-${name}`}
          name={name}
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`w-full rounded-xl border border-slate-200 bg-white py-2.5 pr-3 text-[13px] text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 ${
            Icon ? 'pl-9' : 'pl-3'
          }`}
        />
      </div>
    </div>
  );
}

/*
 * =========================================================
 * CREATE BUSINESS MODAL
 * =========================================================
 */

export default function CreateBusinessModal({
  isOpen,
  onClose
}) {
  const {
    user,
    activeFirm,
    refreshProfile,
    workspaceError
  } = useClient();

  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verified, setVerified] = useState(false);
  const [refreshWarning, setRefreshWarning] = useState('');

  const hasFirm = Boolean(activeFirm?.id);

  const canCreate =
    hasFirm &&
    activeFirm.role === 'owner' &&
    Boolean(user?.id) &&
    !workspaceError;

  /*
   * -------------------------------------------------------
   * MOUNT PORTAL
   * -------------------------------------------------------
   */

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  /*
   * -------------------------------------------------------
   * RESET WHEN OPENED
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!isOpen) return;

    setFormData({ ...INITIAL_FORM });
    setShowMore(false);
    setError('');
    setSuccess(false);
    setVerified(false);
    setRefreshWarning('');
  }, [isOpen]);

  /*
   * -------------------------------------------------------
   * ESCAPE KEY
   * -------------------------------------------------------
   */

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !loading) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener(
        'keydown',
        handleEscape
      );
    };
  }, [isOpen, loading, onClose]);

  /*
   * -------------------------------------------------------
   * FORM HANDLERS
   * -------------------------------------------------------
   */

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value
    }));

    if (error) setError('');
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  /*
   * -------------------------------------------------------
   * SUBMIT TO SUPABASE
   * -------------------------------------------------------
   */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading || success) return;

    if (!canCreate) {
      setError(
        'Only a verified firm owner can add a client.'
      );
      return;
    }

    const validationError = validateForm(formData);

    if (validationError) {
      setError(validationError);
      return;
    }

    const website = formData.website.trim();

    const normalizedWebsite = website
      ? /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`
      : '';

    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      website: normalizedWebsite,
      address: formData.address.trim(),
      country: formData.country.trim(),
      industry: formData.industry.trim(),
      entity_type: formData.entity_type.trim(),
      currency: formData.currency.trim().toUpperCase(),
      fiscal_year_end: formData.fiscal_year_end,
      tax_id: formData.tax_id.trim(),
      year_started: formData.year_started.trim()
    };

    setLoading(true);
    setError('');
    setRefreshWarning('');

    try {
      /*
       * The backend derives auth.uid(), verifies
       * firm-owner membership, creates a client,
       * and links it to the managing firm.
       */
      const response = await settingsService.createAccount(
        payload,
        user.id
      );

      if (!response?.success) {
        throw new Error(
          response?.error ||
          'Unable to create the client.'
        );
      }

      /*
       * Creation succeeded.
       * Access verification is a separate result.
       */
      setVerified(Boolean(response.verified));

      setRefreshWarning(
        response.warning ||
        (response.verified
          ? ''
          : 'The client creation request succeeded, but the new client has not yet been verified in your authorized workspace list. Check the Clients page before trying again.')
      );

      setSuccess(true);

      /*
       * Reload Supabase-backed workspace state.
       * Do not select an unverified client ID.
       */
      if (typeof refreshProfile === 'function') {
        try {
          await refreshProfile();
        } catch (refreshError) {
          console.error(
            'Client created, but refresh failed:',
            refreshError
          );

          setRefreshWarning(
            'The creation request succeeded, but the workspace list could not be refreshed. Check your Clients page before creating the client again.'
          );
        }
      }
    } catch (err) {
      console.error('Create client error:', err);

      setError(
        err?.message ||
        'Unable to create the client.'
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * =======================================================
   * MODAL UI
   * =======================================================
   */

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden p-3 sm:p-6">

          {/* BACKDROP */}
          <motion.div
            key="create-client-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[3px]"
          />

          {/* DIALOG */}
          <motion.div
            key="create-client-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-client-heading"
            initial={{
              opacity: 0,
              y: 14,
              scale: 0.98
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }}
            exit={{
              opacity: 0,
              y: 14,
              scale: 0.98
            }}
            transition={{
              duration: 0.18
            }}
            className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >

            {/* HEADER */}
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-5 sm:px-7">

              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <SafeIcon
                    icon={FiBriefcase}
                    className="text-xl"
                  />
                </div>

                <div className="min-w-0">
                  <h2
                    id="create-client-heading"
                    className="text-lg font-bold tracking-tight text-slate-900"
                  >
                    Add New Client
                  </h2>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    Create a client workspace for your accounting firm.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                aria-label="Close modal"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiX}
                  className="text-lg"
                />
              </button>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">

              {success ? (
                <div
                  role="status"
                  className="flex flex-col items-center py-10 text-center"
                >
                  <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="text-3xl"
                    />
                  </div>

                  <h3 className="text-xl font-bold text-slate-900">
                    {verified
                      ? 'Client Created'
                      : 'Creation Request Succeeded'}
                  </h3>

                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
                    {verified
                      ? 'Your new client is now available through your authorized accounting workspace.'
                      : 'The creation request completed. Please confirm the new client appears in your workspace before trying again.'}
                  </p>

                  {refreshWarning && (
                    <div className="mt-5 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 p-3 text-left text-xs leading-relaxed text-amber-800">
                      <SafeIcon
                        icon={FiAlertCircle}
                        className="mt-0.5 shrink-0"
                      />

                      <p>{refreshWarning}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleClose}
                    className="mt-6 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form
                  id="create-business-form"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >

                  {/* MANAGING FIRM */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center gap-3">

                      <SafeIcon
                        icon={FiShield}
                        className="shrink-0 text-base text-slate-500"
                      />

                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-slate-500">
                          Managing Firm
                        </p>

                        <p className="truncate text-[13px] font-semibold text-slate-900">
                          {hasFirm
                            ? activeFirm.name
                            : 'No verified firm workspace'}
                        </p>
                      </div>

                      {canCreate && (
                        <SafeIcon
                          icon={FiCheckCircle}
                          className="text-emerald-600"
                        />
                      )}
                    </div>
                  </div>

                  {/* ERROR */}
                  {(error || workspaceError) && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50 p-3 text-xs leading-relaxed text-rose-700"
                    >
                      <SafeIcon
                        icon={FiAlertCircle}
                        className="mt-0.5 shrink-0 text-base"
                      />

                      <p>{error || workspaceError}</p>
                    </div>
                  )}

                  {/* BASIC DETAILS */}
                  <section className="space-y-4">
                    <div>
                      <h3 className="text-[13px] font-bold text-slate-900">
                        Business Details
                      </h3>

                      <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        Country, currency, and fiscal year use
                        your firm's settings when left blank.
                      </p>
                    </div>

                    <FormField
                      label="Business Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter business name"
                      icon={FiBriefcase}
                      maxLength={120}
                      required
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        label="Business Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Business email"
                        icon={FiMail}
                      />

                      <FormField
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Contact number"
                        icon={FiPhone}
                      />
                    </div>

                    <FormField
                      label="Website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="company.com"
                      icon={FiGlobe}
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        label="Country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="Country of registration"
                        icon={FiMapPin}
                      />

                      <FormField
                        label="Base Currency"
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        placeholder="Currency code"
                        maxLength={3}
                      />
                    </div>
                  </section>

                  {/* OPTIONAL DETAILS */}
                  <section className="border-t border-slate-100 pt-5">
                    <button
                      type="button"
                      onClick={() =>
                        setShowMore((previous) => !previous)
                      }
                      aria-expanded={showMore}
                      className="flex w-full items-center justify-between gap-3 rounded-lg py-1 text-left"
                    >
                      <div>
                        <h3 className="text-[13px] font-bold text-slate-900">
                          Additional Information
                        </h3>

                        <p className="mt-1 text-xs text-slate-500">
                          Optional details can be completed later.
                        </p>
                      </div>

                      <SafeIcon
                        icon={FiChevronDown}
                        className={`text-slate-400 transition-transform ${
                          showMore ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {showMore && (
                        <motion.div
                          initial={{
                            height: 0,
                            opacity: 0
                          }}
                          animate={{
                            height: 'auto',
                            opacity: 1
                          }}
                          exit={{
                            height: 0,
                            opacity: 0
                          }}
                          transition={{
                            duration: 0.18
                          }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-4 pt-5">

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <FormField
                                label="Industry"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                placeholder="Business industry"
                              />

                              <FormField
                                label="Entity Type"
                                name="entity_type"
                                value={formData.entity_type}
                                onChange={handleChange}
                                placeholder="Legal entity type"
                              />
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <FormField
                                label="Tax ID"
                                name="tax_id"
                                value={formData.tax_id}
                                onChange={handleChange}
                                placeholder="Tax registration number"
                              />

                              <FormField
                                label="Year Started"
                                name="year_started"
                                type="number"
                                value={formData.year_started}
                                onChange={handleChange}
                                placeholder="Year established"
                              />
                            </div>

                            <div className="space-y-1.5">
                              <label
                                htmlFor="client-fiscal-year"
                                className="block text-xs font-semibold text-slate-700"
                              >
                                Fiscal Year End
                              </label>

                              <select
                                id="client-fiscal-year"
                                name="fiscal_year_end"
                                value={formData.fiscal_year_end}
                                onChange={handleChange}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none focus:border-emerald-500"
                              >
                                <option value="">
                                  Use firm setting
                                </option>

                                {MONTHS.map((month) => (
                                  <option
                                    key={month}
                                    value={month}
                                  >
                                    {month}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-1.5">
                              <label
                                htmlFor="client-address"
                                className="block text-xs font-semibold text-slate-700"
                              >
                                Business Address
                              </label>

                              <textarea
                                id="client-address"
                                name="address"
                                rows={3}
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Street address, city, state or province, postal code"
                                className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[13px] text-slate-900 outline-none focus:border-emerald-500"
                              />
                            </div>

                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                </form>
              )}
            </div>

            {/* FOOTER */}
            {!success && (
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-white px-5 py-4 sm:px-7">

                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  form="create-business-form"
                  disabled={loading || !canCreate}
                  className="inline-flex min-w-[145px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <SafeIcon
                        icon={FiLoader}
                        className="animate-spin"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiPlus} />
                      Create Client
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(modalContent, document.body);
}